import { Button } from '@/components/ui/button';
import { FileDown } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell } from 'recharts';

interface Props {
  brand: string;
  product: string;
  priceLaunch: number;
  priceTarget: number;
  cogs: number;
  fba: number;
  kpiLaunch: {
    grossMarginPct: number;
    netNoAdsPct: number;
    breakEvenPct: number;
    acosLaunch: number;
    roasLaunch: number | null;
    budgetLaunch: number;
    marginAfterAdsLaunch: number;
  };
  kpiTarget: {
    grossMarginPct: number;
    netNoAdsPct: number;
    breakEvenPct: number;
    acosOptimize: number;
    roasOptimize: number | null;
    budgetOptimize: number;
    marginAfterAdsOptimize: number;
  };
  waterfallDataLaunch: Array<{ name: string; value: number; }>;
  waterfallDataTarget: Array<{ name: string; value: number; }>;
  acosRoasData: Array<{ phase: string; acos: number; roas: number; }>;
  advancedParams: {
    cpcEur: { launch: number; growth: number; optimize: number; };
    cvr: { launch: number; growth: number; optimize: number; };
    shareAds: { launch: number; growth: number; optimize: number; };
    tacosCap: { launch: number; growth: number; optimize: number; };
  };
  budgetMin: number;
  budgetMax: number;
  budgetPhaseLaunch: number;
  budgetPhaseTarget: number;
  logoSrc?: string;
}

const A4 = { W: 595, H: 842, m: 28 };
const pageW = A4.W - A4.m * 2;
const pageH = A4.H - A4.m * 2;

const euro = (v: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v);
const pct = (v: number) => `${(v * 100).toFixed(1)} %`;

// Composants utilitaires
const Kpi = ({ title, val, cap }: { title: string; val: string; cap: string }) => (
  <div className="border border-zinc-200 rounded-lg p-2">
    <div className="font-semibold text-xs" style={{ color: '#6b7280' }}>{title}</div>
    <div className="font-extrabold text-base leading-none my-1">{val}</div>
    <div className="text-[10pt] leading-tight" style={{ color: '#6b7280' }}>{cap}</div>
  </div>
);

const TableUnitEconomics = ({ rows }: { rows: [string, string][] }) => (
  <table className="w-full text-[12pt] border border-zinc-200 rounded-lg">
    <tbody>
      {rows.map(([k, v]) => (
        <tr key={k} className="border-b last:border-b-0 border-zinc-200">
          <td className="py-1 px-2 text-zinc-600">{k}</td>
          <td className="py-1 px-2 font-semibold text-right">{v}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Reco = ({ title, bullets }: { title: string; bullets: string[] }) => (
  <div className="border border-zinc-200 rounded-lg p-3">
    <div className="font-semibold mb-2 text-sm" style={{ color: '#6b7142' }}>{title}</div>
    <ul className="list-disc list-inside text-[11pt] leading-snug space-y-0.5">
      {bullets.map((b, i) => <li key={i} className="text-zinc-700">{b}</li>)}
    </ul>
  </div>
);

const InfoCard = ({ title, items }: { title: string; items: string[] }) => (
  <div className="border border-zinc-200 rounded-lg p-3">
    <div className="font-semibold mb-2 text-sm" style={{ color: '#6b7142' }}>{title}</div>
    <ul className="list-disc list-inside text-[11pt] leading-snug space-y-0.5">
      {items.map((t, i) => <li key={i} className="text-zinc-700">{t}</li>)}
    </ul>
  </div>
);

const MiniWaterfall = ({ data }: { data: Array<{ name: string; value: number }> }) => {
  const colors = ['#10b981', '#ef4444', '#ef4444', '#ef4444', '#6b7142'];
  return (
    <div>
      <div className="font-semibold text-sm mb-1" style={{ color: '#6b7142' }}>Décomposition marge (Lancement)</div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip contentStyle={{ fontSize: '11px', backgroundColor: '#fff', border: '1px solid #e5e7eb' }} />
          <Bar dataKey="value">
            {data.map((_, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const MiniAcosRoas = ({ data }: { data: Array<{ phase: string; acos: number; roas: number }> }) => (
  <div>
    <div className="font-semibold text-sm mb-1" style={{ color: '#6b7142' }}>Évolution ACOS & ROAS par phase</div>
    <ResponsiveContainer width="100%" height={200}>
      <LineChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="phase" tick={{ fontSize: 10 }} />
        <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
        <Tooltip contentStyle={{ fontSize: '11px', backgroundColor: '#fff', border: '1px solid #e5e7eb' }} />
        <Legend wrapperStyle={{ fontSize: '10px' }} />
        <Line yAxisId="left" type="monotone" dataKey="acos" stroke="#ef4444" strokeWidth={2} name="ACOS (%)" />
        <Line yAxisId="right" type="monotone" dataKey="roas" stroke="#10b981" strokeWidth={2} name="ROAS (×)" />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

export default function PdfReportAmzAdsMinimal({
  brand,
  product,
  priceLaunch,
  priceTarget,
  cogs,
  fba,
  kpiLaunch,
  kpiTarget,
  waterfallDataLaunch,
  waterfallDataTarget,
  acosRoasData,
  advancedParams,
  budgetMin,
  budgetMax,
  budgetPhaseLaunch,
  budgetPhaseTarget,
  logoSrc
}: Props) {

  const exportPDFOne = async () => {
    const src = document.getElementById('pdf-one');
    if (!src) {
      alert('Bloc #pdf-one introuvable');
      return;
    }

    // 1) Créer le sandbox s'il n'existe pas
    let sandbox = document.getElementById('pdf-sandbox');
    if (!sandbox) {
      sandbox = document.createElement('div');
      sandbox.id = 'pdf-sandbox';
      sandbox.style.position = 'fixed';
      sandbox.style.left = '-99999px';
      sandbox.style.top = '0';
      sandbox.style.width = '595pt';
      sandbox.style.zIndex = '-1';
      document.body.appendChild(sandbox);
    } else {
      sandbox.innerHTML = '';
    }

    // 2) Cloner le contenu pour qu'il soit VISIBLE
    const clone = src.cloneNode(true) as HTMLElement;
    clone.classList.add('pdf-force-light');
    sandbox.appendChild(clone);

    // 3) S'assurer que toutes les images sont chargées
    const imgs = Array.from(clone.querySelectorAll('img')) as HTMLImageElement[];
    await Promise.all(
      imgs.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) return resolve();
            img.onload = () => resolve();
            img.onerror = () => resolve();
          })
      )
    );

    // Attendre un peu pour Recharts
    await new Promise((r) => setTimeout(r, 600));

    // 4) Calculer une échelle pour tenir sur 1 page A4
    const rect = clone.getBoundingClientRect();
    const scale = Math.min(1, (A4.H - A4.m * 2) / rect.height, (A4.W - A4.m * 2) / rect.width);

    clone.style.transformOrigin = 'top left';
    clone.style.transform = `scale(${scale})`;

    // 5) Export html2pdf
    const now = new Date().toLocaleDateString('fr-FR');
    const opt = {
      margin: [A4.m, A4.m, A4.m, A4.m] as [number, number, number, number],
      filename: `Strategie_Amazon_Ads_${brand}_${product}_${now}.pdf`,
      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: Math.max(rect.width, A4.W),
        windowHeight: Math.max(rect.height, A4.H),
        logging: false
      },
      jsPDF: { unit: 'pt', format: 'a4', compress: true },
      pagebreak: { mode: ['css', 'avoid-all', 'legacy'] }
    } as const;

    try {
      await html2pdf().set(opt).from(clone).save();
      console.log('PDF généré avec succès');
    } catch (error) {
      console.error('Erreur génération PDF:', error);
    }

    // 6) Nettoyage
    clone.style.transform = '';
    sandbox.innerHTML = '';
  };

  const commLaunch = priceLaunch * (priceLaunch <= 10 ? 0.08 : 0.15);
  const commTarget = priceTarget * (priceTarget <= 10 ? 0.08 : 0.15);

  return (
    <div className="space-y-6">
      {/* CSS pour forcer le thème clair dans le PDF */}
      <style>{`
        .pdf-force-light,
        .pdf-force-light * {
          background: #ffffff !important;
          color: #0f1113 !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        #pdf-sandbox {
          position: fixed;
          left: -99999px;
          top: 0;
          width: 595pt;
          z-index: -1;
        }
      `}</style>

      {/* Bouton Export */}
      <div className="text-center">
        <Button
          onClick={exportPDFOne}
          className="px-6 py-3 text-white font-semibold rounded-xl"
          style={{ background: '#6b7142' }}
          size="lg"
        >
          <FileDown className="w-5 h-5 mr-2" />
          Exporter PDF (1 page)
        </Button>
      </div>

      {/* Conteneur PDF */}
      <div
        id="pdf-one"
        className="bg-white text-zinc-900 rounded-xl p-6 flex flex-col justify-between gap-3 pdf-force-light"
        style={{ width: `${pageW}pt`, minHeight: `${pageH}pt`, fontSize: '14pt' }}
      >
        {/* HEADER */}
        <header className="space-y-2">
          {/* Ligne marque */}
          <div className="flex items-start justify-between">
            <div /> {/* vide pour pousser à droite */}
            <div className="text-right text-sm">
              <span className="font-extrabold" style={{color:'#6b7142'}}>Cosmos Wave</span> <span>marketing</span>
            </div>
          </div>

          {/* Titre & sous-titre */}
          <h1 className="text-xl font-extrabold" style={{ color: '#6b7142' }}>
            Stratégie Amazon Ads personnalisée
          </h1>
          <div className="text-sm" style={{color:'#6b7280'}}>
            Marque : <b>{brand}</b> • Produit : <b>{product}</b>
          </div>

          {/* Introduction marketing */}
          <p className="text-[12pt] leading-snug" style={{color:'#0f1113'}}>
            Cette recommandation publicitaire s'appuie sur nos hypothèses média (CPC, CVR) et sur la marge nette Amazon.
            L'objectif : sécuriser la rentabilité (ACOS ≤ break-even) tout en construisant progressivement la part organique.
          </p>

          {/* Données essentielles */}
          <table className="w-full text-[12pt] border border-zinc-200 rounded-lg">
            <tbody>
              <tr className="border-b border-zinc-200">
                <td className="py-1 px-2 text-zinc-600">Prix TTC (Lancement / Cible)</td>
                <td className="py-1 px-2 font-semibold text-right">{euro(priceLaunch)} / {euro(priceTarget)}</td>
              </tr>
              <tr className="border-b border-zinc-200">
                <td className="py-1 px-2 text-zinc-600">COGS</td>
                <td className="py-1 px-2 font-semibold text-right">{euro(cogs)}</td>
              </tr>
              <tr className="border-b border-zinc-200">
                <td className="py-1 px-2 text-zinc-600">Commission Amazon</td>
                <td className="py-1 px-2 font-semibold text-right">{priceLaunch <= 10 ? '8 %' : '15 %'}</td>
              </tr>
              <tr>
                <td className="py-1 px-2 text-zinc-600">FBA estimé</td>
                <td className="py-1 px-2 font-semibold text-right">{euro(fba)}</td>
              </tr>
            </tbody>
          </table>
        </header>

        {/* BODY — 2 colonnes */}
        <section className="grid grid-cols-2 gap-6">
          {/* Colonne 1 : Scénario Prix Lancement */}
          <div className="space-y-3">
            <h2 className="font-semibold text-base" style={{ color: '#6b7142' }}>
              Scénario Prix Lancement — {euro(priceLaunch)}
            </h2>

            {/* KPIs */}
            <div className="grid grid-cols-2 gap-2">
              <Kpi title="Marge brute" val={pct(kpiLaunch.grossMarginPct)} cap="(Prix − COGS) / Prix" />
              <Kpi title="Marge nette (hors pub)" val={pct(kpiLaunch.netNoAdsPct)} cap="(Prix − COGS − Comm. − FBA) / Prix" />
              <Kpi title="ACOS break-even" val={pct(kpiLaunch.breakEvenPct)} cap="Seuil de rentabilité pub" />
              <Kpi title="Marge après pub" val={pct(kpiLaunch.marginAfterAdsLaunch)} cap="Après budget phase active" />
            </div>

            {/* Tableau Économie unitaire */}
            <TableUnitEconomics
              rows={[
                ['Prix TTC', euro(priceLaunch)],
                ['COGS', euro(cogs)],
                ['Commission', euro(commLaunch)],
                ['FBA', euro(fba)],
                ['Marge nette (hors pub)', euro(kpiLaunch.netNoAdsPct * priceLaunch)]
              ]}
            />

            {/* Recommandation stratégique */}
            <Reco
              title="Recommandation (60–90 jours)"
              bullets={[
                "Objectif : traction et données fiables, bases organiques.",
                "Mix : SP Auto (4), SP Manuel Exact/Broad (10–20 KW cœur), SB Marque, SD ciblage produits T2.",
                "Pilotage : négatifs hebdo, +20–40 % Top-of-Search si CR > médiane, rules/placements, dayparting si utile.",
                "Créa & vitrine : A+ actif, Brand Store minimal, hooks USP en SB Video.",
                `Budget crédible : ${euro(budgetPhaseLaunch)} (cap TACoS ${(advancedParams.tacosCap.launch * 100).toFixed(0)} %).`
              ]}
            />
          </div>

          {/* Colonne 2 : Scénario Prix Cible */}
          <div className="space-y-3">
            <h2 className="font-semibold text-base" style={{ color: '#6b7142' }}>
              Scénario Prix Cible — {euro(priceTarget)}
            </h2>

            {/* KPIs */}
            <div className="grid grid-cols-2 gap-2">
              <Kpi title="Marge brute" val={pct(kpiTarget.grossMarginPct)} cap="(Prix − COGS) / Prix" />
              <Kpi title="Marge nette (hors pub)" val={pct(kpiTarget.netNoAdsPct)} cap="(Prix − COGS − Comm. − FBA) / Prix" />
              <Kpi title="ACOS break-even" val={pct(kpiTarget.breakEvenPct)} cap="Seuil de rentabilité pub" />
              <Kpi title="Marge après pub" val={pct(kpiTarget.marginAfterAdsOptimize)} cap="Après budget phase active" />
            </div>

            {/* Tableau Économie unitaire */}
            <TableUnitEconomics
              rows={[
                ['Prix TTC', euro(priceTarget)],
                ['COGS', euro(cogs)],
                ['Commission', euro(commTarget)],
                ['FBA', euro(fba)],
                ['Marge nette (hors pub)', euro(kpiTarget.netNoAdsPct * priceTarget)]
              ]}
            />

            {/* Recommandation stratégique */}
            <Reco
              title="Recommandation (3–6 mois)"
              bullets={[
                "Objectif : baisser le TACoS et augmenter la part organique.",
                "Mix : SP Exact (gagnants), SB Video (upper-mid), SD remarketing 7–30 j, expansion KW L2/L3.",
                "Optimisation : harvesting → Exact, négatifs systématiques, reallocation budgétaire mensuelle (PBR).",
                `Budget crédible : ${euro(budgetPhaseTarget)} (cap TACoS ${(advancedParams.tacosCap.optimize * 100).toFixed(0)} %).`
              ]}
            />
          </div>
        </section>

        {/* CHARTS COMPACTS */}
        <section className="grid grid-cols-2 gap-6">
          <MiniWaterfall data={waterfallDataLaunch} />
          <MiniAcosRoas data={acosRoasData} />
        </section>

        {/* HYPOTHÈSES & CAPS */}
        <section className="grid grid-cols-3 gap-4">
          <InfoCard
            title="Hypothèses média"
            items={[
              `CPC moyen : ${euro(advancedParams.cpcEur.launch)}`,
              `CVR : ${(advancedParams.cvr.launch * 100).toFixed(0)} %`,
              `Part ventes Ads : ${(advancedParams.shareAds.launch * 100).toFixed(0)} %`
            ]}
          />
          <InfoCard
            title="Caps & crédibilité"
            items={[
              `Cap TACoS : ${(advancedParams.tacosCap.launch * 100).toFixed(0)} %`,
              `ACOS cible ≤ break-even − 5 pts`
            ]}
          />
          <InfoCard
            title="Budget recommandé"
            items={[
              `Phase Lancement : ${euro(budgetPhaseLaunch)}`,
              `Plage (phases) : ${euro(budgetMin)} – ${euro(budgetMax)}`
            ]}
          />
        </section>

        {/* FOOTER */}
        <footer className="text-center text-sm pt-2 border-t border-zinc-200" style={{ color: '#6b7142' }}>
          Cosmos Wave • Simulateur Amazon Ads © 2025 • {new Date().toLocaleString('fr-FR')}
        </footer>
      </div>
    </div>
  );
}
